/**
 * Support at Home Strategy Evaluator
 *
 * Ranks already-evaluated strategies (from the server-side composite endpoint,
 * calculationApi.evaluateSupportAtHomeStrategies) by goal priority.
 */
import type { EvaluatedStrategy } from './supportAtHomeRecommendedStrategies.ts';

export type GoalPriority = 'cashflow' | 'pension' | 'estate' | null | undefined;

export interface BaselineAnalysis {
    problemStatement?: {
        pensionOptimization?: { status?: string };
        contributionOptimization?: { ratesAtMinimum?: boolean };
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

export interface RankedStrategy extends EvaluatedStrategy {
    score: number;
}

/**
 * Calculate strategy score based on goal priority
 */
export function calculateStrategyScore(strategy: EvaluatedStrategy, goalPriority: GoalPriority, _baselineAnalysis: BaselineAnalysis | null | undefined): number {
    const s = strategy.snapshot || {};

    // Normalize values to prevent one metric from dominating
    const normalizeCashflow = (value: number) => value / 10000; // $10K units
    const normalizePension = (value: number) => value / 5000; // $5K units
    const normalizeContribution = (value: number) => value / 5000; // $5K units
    const normalizeLegacy = (value: number) => value / 50000; // $50K units

    if (goalPriority === 'cashflow') {
        return (
            normalizeCashflow(s.cashflowImprovement || 0) * 0.5 +
            normalizeContribution(s.contributionReduction || 0) * 0.3 +
            normalizePension(s.agePensionIncrease || 0) * 0.2
        );
    } else if (goalPriority === 'pension') {
        return (
            normalizePension(s.agePensionIncrease || 0) * 0.5 +
            normalizeContribution(s.contributionReduction || 0) * 0.3 +
            (1 - (s.contributionRates?.everydayLiving || 0)) * 0.2 // Lower rate = higher score
        );
    } else if (goalPriority === 'estate') {
        return (
            normalizeLegacy(s.legacyImprovement || 0) * 0.5 +
            normalizeContribution(s.contributionReduction || 0) * 0.3 +
            normalizeCashflow(s.cashflowImprovement || 0) * 0.2
        );
    } else {
        // Balanced scoring (all goals equal)
        return (
            normalizeCashflow(s.cashflowImprovement || 0) * 0.4 +
            normalizePension(s.agePensionIncrease || 0) * 0.3 +
            normalizeLegacy(s.legacyImprovement || 0) * 0.3
        );
    }
}

/**
 * Rank strategies by goal priority
 */
export function rankStrategies(strategies: EvaluatedStrategy[], goalPriority: GoalPriority, baselineAnalysis: BaselineAnalysis | null | undefined): RankedStrategy[] {
    // Calculate scores for all strategies
    const strategiesWithScores: RankedStrategy[] = strategies.map(strategy => ({
        ...strategy,
        score: calculateStrategyScore(strategy, goalPriority, baselineAnalysis)
    }));

    // Filter out strategies that worsen key metrics without meaningful benefit
    const viableStrategies = strategiesWithScores.filter(strategy => {
        if (strategy.name === 'Do Nothing' || strategy.config?.type === 'doNothing') {
            return true;
        }

        const s = strategy.snapshot || {};

        // Get improvements (negative values mean worsening)
        const cashflowImprovement = s.cashflowImprovement || 0;
        const pensionIncrease = s.agePensionIncrease || 0;
        const contributionReduction = s.contributionReduction || 0;
        const legacyImprovement = s.legacyImprovement || 0;

        // Count improvements and deteriorations
        const improvements = [
            cashflowImprovement > 0,
            pensionIncrease > 0,
            contributionReduction > 0,
            legacyImprovement > 0
        ].filter(Boolean).length;

        const deteriorations = [
            cashflowImprovement < 0,
            pensionIncrease < 0,
            contributionReduction < 0,
            legacyImprovement < 0
        ].filter(Boolean).length;

        // Exclude strategies that:
        // 1. Worsen ALL key metrics (no improvement in any area)
        if (improvements === 0) {
            return false;
        }

        // 2. Worsen cashflow significantly (>$10K) without improving pension or contributions
        // AND only provide small legacy benefit (<$50K)
        if (cashflowImprovement < -10000 &&
            pensionIncrease <= 0 &&
            contributionReduction <= 0 &&
            legacyImprovement < 50000) {
            return false;
        }

        // 3. Worsen multiple key metrics (2+) and only improve legacy slightly
        if (deteriorations >= 2 &&
            improvements === 1 &&
            legacyImprovement < 100000 &&
            (pensionIncrease <= 0 && contributionReduction <= 0)) {
            return false;
        }

        // 4. Special case: If client is on full pension and minimum contribution rates,
        // renovate strategies should only be recommended if they significantly improve cashflow
        // (since they can't improve pension or contributions further)
        const isHomeEquityStrategy = ['increaseHomeEquity', 'renovate', 'upgrade'].includes(String(strategy.config?.type));
        const isFullPension = baselineAnalysis?.problemStatement?.pensionOptimization?.status === 'full';
        const isMinContributions = baselineAnalysis?.problemStatement?.contributionOptimization?.ratesAtMinimum === true;

        if (isHomeEquityStrategy && isFullPension && isMinContributions) {
            // Only keep if cashflow improvement is significant (>$5K) OR legacy improvement is very large (>$200K)
            if (cashflowImprovement <= 5000 && legacyImprovement < 200000) {
                return false;
            }
        }

        // Keep strategies that have meaningful improvements
        return true;
    });

    // Sort by score (descending)
    viableStrategies.sort((a, b) => (b.score || 0) - (a.score || 0));

    return viableStrategies;
}
